/**
 * I16 — minimal keyring used by the lock menu.
 *
 * The real character-inventory accrual path (C-series) is still landing —
 * `inventory:give_item` currently only drives a feed line. To let the lock
 * menu check "does the party have a matching key?", this store listens for
 * `inventory:give_item` and remembers every item id the party has received.
 * When character inventories come online, this can shrink to a thin wrapper
 * over them (or be retired entirely).
 */

import { EventBus } from "@engine/EventBus.js";

const items = new Set<string>();
let listenerAttached = false;

export interface KeyringStoreJSON {
  items: string[];
}

export const KeyringStore = {
  /**
   * Subscribe to `inventory:give_item` and `inventory:item_acquired` (the
   * observer-only channel used by paths that already wrote the inventory,
   * e.g. loot-all). Idempotent — safe to call from the boot path multiple
   * times (HMR, tests).
   */
  init(): void {
    if (listenerAttached) return;
    listenerAttached = true;
    EventBus.on("inventory:give_item", ({ itemId }) => {
      items.add(itemId);
    });
    EventBus.on("inventory:item_acquired", ({ itemId }) => {
      items.add(itemId);
    });
  },

  /** Add an item id directly (used by tests and future inventory hooks). */
  add(itemId: string): void {
    items.add(itemId);
  },

  /**
   * Remove an item id (I17 — lockpick failure consumes Thieves' Tools).
   * No-ops when the id isn't present.
   */
  remove(itemId: string): void {
    items.delete(itemId);
  },

  /** True when the party has ever received an item with this id. */
  has(itemId: string): boolean {
    return items.has(itemId);
  },

  /** Snapshot of received item ids (sorted for determinism in saves/tests). */
  list(): readonly string[] {
    return [...items].sort();
  },

  reset(): void {
    items.clear();
  },

  toJSON(): KeyringStoreJSON {
    return { items: [...items].sort() };
  },

  loadJSON(data: KeyringStoreJSON): void {
    items.clear();
    for (const id of data.items) items.add(id);
  },
};
